// frontend/src/tabs/storage.js
//
// sessionStorage adapter for the tabbed workspace.
//
// Three families of keys live here:
//   manexys.tabs.list           — array of tab descriptors
//   manexys.tabs.active         — id of the currently focused tab
//   manexys.tabs.<tabId>.<key>  — per-tab slot: any view state scoped to one
//                                 tab rather than to the user. Filter
//                                 selections (useTabPersistedState) and grid
//                                 view state (useGridState) both live here.
//
// All access goes through this module so the storage layout is in one place.
// A failed read or write logs console.error and returns a safe default
// (undefined for reads, no-op for writes): this is per-tab view state, and a
// quota or disabled-storage failure must not crash the app.

const TABS_LIST_KEY = 'manexys.tabs.list';
const TABS_ACTIVE_KEY = 'manexys.tabs.active';
const TAB_SLOT_PREFIX = 'manexys.tabs.';
const TAB_SLOT_SEPARATOR = '.';

export const HOME_TAB_ID = 'home';
export const HOME_TAB_PATH = '/';

function safeGetItem(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    console.error('tabs/storage: sessionStorage read failed for', key, err);
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    console.error('tabs/storage: sessionStorage write failed for', key, err);
  }
}

function safeRemoveItem(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch (err) {
    console.error('tabs/storage: sessionStorage remove failed for', key, err);
  }
}

export function seedHomeTab() {
  return {
    id: HOME_TAB_ID,
    path: HOME_TAB_PATH,
    openedAt: Date.now(),
  };
}

// Load the tab list and active id from sessionStorage. If anything is
// missing, malformed, or doesn't include a Home tab at HOME_TAB_PATH, repair
// locally and return a valid state. Never throws — a corrupted storage entry
// should degrade to "fresh session" rather than crashing the app.
export function loadTabsState() {
  const fallback = { tabs: [seedHomeTab()], activeId: HOME_TAB_ID };

  const rawList = safeGetItem(TABS_LIST_KEY);
  const rawActive = safeGetItem(TABS_ACTIVE_KEY);

  if (!rawList) return fallback;

  let parsed;
  try {
    parsed = JSON.parse(rawList);
  } catch (err) {
    console.error('tabs/storage: malformed tabs list, resetting', err);
    return fallback;
  }

  if (!Array.isArray(parsed) || parsed.length === 0) return fallback;

  const valid = parsed.filter(
    (t) => t && typeof t.id === 'string' && typeof t.path === 'string',
  );
  if (valid.length === 0) return fallback;

  const hasHome = valid.some((t) => t.id === HOME_TAB_ID);
  const restored = hasHome ? valid : [seedHomeTab(), ...valid];
  // Home's path is a constant, not restorable state: the stored entry keeps
  // its title and openedAt, but its path is re-pinned to HOME_TAB_PATH. A
  // stored home tab pointing anywhere else would be a second answer to "where
  // does Home live" — and it is load-bearing, because the store switch
  // restarts the document at the surviving tab's path
  // (useTabs.closeAllExceptHome), and TabStrip opens a tab for a path no
  // restored tab matches.
  const tabs = restored.map(
    (t) => (t.id === HOME_TAB_ID ? { ...t, path: HOME_TAB_PATH } : t),
  );

  const requestedActive = rawActive || HOME_TAB_ID;
  const activeId = tabs.some((t) => t.id === requestedActive)
    ? requestedActive
    : HOME_TAB_ID;

  return { tabs, activeId };
}

export function saveTabsState({ tabs, activeId }) {
  safeSetItem(TABS_LIST_KEY, JSON.stringify(tabs));
  safeSetItem(TABS_ACTIVE_KEY, activeId);
}

function slotKey(tabId, key) {
  return `${TAB_SLOT_PREFIX}${tabId}${TAB_SLOT_SEPARATOR}${key}`;
}

// Returns the stored value (any JSON-serialisable) or undefined if nothing
// is stored / parse fails. Callers fall back to their initial value when
// they get undefined.
export function loadTabSlot(tabId, key) {
  const raw = safeGetItem(slotKey(tabId, key));
  if (raw === null) return undefined;
  try {
    return JSON.parse(raw);
  } catch (err) {
    console.error('tabs/storage: malformed slot', tabId, key, err);
    return undefined;
  }
}

export function saveTabSlot(tabId, key, value) {
  safeSetItem(slotKey(tabId, key), JSON.stringify(value));
}

function removeByPrefix(prefix) {
  let storage;
  try {
    storage = window.sessionStorage;
  } catch (err) {
    console.error('tabs/storage: cannot sweep', prefix, '— storage unavailable', err);
    return;
  }
  const toRemove = [];
  for (let i = 0; i < storage.length; i += 1) {
    const k = storage.key(i);
    if (k && k.startsWith(prefix)) toRemove.push(k);
  }
  toRemove.forEach(safeRemoveItem);
}

// On tab close, sweep all sessionStorage entries belonging to that tab so
// orphan keys don't accumulate during a long browser session.
export function clearTabSlots(tabId) {
  removeByPrefix(`${TAB_SLOT_PREFIX}${tabId}${TAB_SLOT_SEPARATOR}`);
}

// Drop the whole workspace: the tab list, the focused tab, and every per-tab
// slot. For SESSION boundaries, where nothing about the previous session may
// survive — logout is one, and it is not a document replacement, so the next
// user signing in on this browser tab would otherwise restore the previous
// user's open tabs along with their filter selections and grid state. Every
// key this module owns lives under one prefix, so the sweep is the whole
// contract: the three key families cannot drift apart from it.
export function clearWorkspace() {
  removeByPrefix(TAB_SLOT_PREFIX);
}
